import React from 'react'
import { makeStyles } from '@material-ui/core'
import { useWizard } from 'react-use-wizard'
import cx from 'classnames'
import { useState } from 'react'
import {
    CUSTOM_ROLE,
    DEFAULT_ROLES,
    RELATIONS,
    USER_RELATIONS
} from 'web/src/app/root/constants'
import { CreateRole } from './create-role/create-role'
import { PageTitle, Button } from 'web/src/app/root/components/common'
import {
    AdminUser,
    TechnicianUser,
    ViewerUser,
    ManagerUser,
    CustomUser
} from '../../../icons'
import { useSelector } from 'react-redux'
import { useBooleanState } from 'web/src/utils/hooks/useBooleanState'
import { useCreateUser } from '../../context/create-user-context'
import { Loading, Picker } from '../../../../../root/components/common'
import { getRoleName } from 'web/src/utils'

export const SelectRole = ({ defaultRoles, isLoadingRoles, isRolesError }) => {
    const { nextStep, previousStep } = useWizard()
    const {
        locale,
        user: { original_customer_id, original_role }
    } = useSelector(state => state.main)

    const styles = useStyles()
    const { userState, onUserUpdate, userRelation, userRelationFromEmail } =
        useCreateUser()
    const [selectedRole, setSelectedRole] = useState(
        userState.role && !userState.role_code
            ? CUSTOM_ROLE
            : userState.role_code || ''
    )
    const [roleState, setRoleState] = useState(
        userState.role ? userState.role.components : {}
    )

    const availableCustomRoles = useSelector(state =>
        state.userManagement.roles
            ? state.userManagement.roles.reduce((acc, role) => {
                  if (
                      !Object.keys(locale.userManagement.defaultRoles).includes(
                          role.code
                      )
                  ) {
                      acc[role.code] = getRoleName(role, locale)
                  }
                  return acc
              }, {})
            : null
    )
    const [selectedCustomRole, setSelectedCustomRole] = useState(
        availableCustomRoles &&
            selectedRole &&
            availableCustomRoles[selectedRole]
            ? selectedRole
            : null
    )

    const [name, setName] = useState(userState.role ? userState.role.name : '')
    const pristine = useBooleanState(true)

    const isValid = () => {
        if (isCustomRoleSelect) {
            return Boolean(name)
        }
        return Boolean(selectedRole)
    }

    const handleNextStep = () => {
        pristine.off()
        if (!isValid()) {
            return
        }
        onUserUpdate({
            ...(selectedRole === CUSTOM_ROLE
                ? {
                      role: {
                          components: roleState,
                          name,
                          customer_id: original_customer_id
                      },
                      role_code: undefined
                  }
                : {
                      role_code: selectedRole,
                      role: undefined
                  })
        })
        nextStep()
    }

    const isCustomRoleSelect = selectedRole === CUSTOM_ROLE

    const handleSelectRole = code => {
        setSelectedCustomRole(null)
        setSelectedRole(code)
    }

    const handleSelectCustomRole = code => {
        setSelectedRole(code)
        setSelectedCustomRole(code)
    }

    const handleCreateCustomRole = () => {
        setSelectedRole(CUSTOM_ROLE)
    }

    if (isLoadingRoles) {
        return (
            <div
                className={styles.root}
                style={{ alignItems: 'center', flexDirection: 'row' }}>
                <Loading />
            </div>
        )
    }

    if (!isLoadingRoles && isRolesError) {
        return (
            <div
                className={styles.root}
                style={{ alignItems: 'center', flexDirection: 'row' }}>
                {locale.sagasMsgs.commonLoadingError}
            </div>
        )
    }

    const options = getOptionsForCurrentRole(
        original_role,
        userRelation,
        userRelationFromEmail,
        defaultRoles
    )

    return (
        <div className={styles.root}>
            {isCustomRoleSelect ? (
                <CreateRole
                    error={!pristine.is && !name}
                    name={name}
                    setName={setName}
                    roleState={roleState}
                    setRoleState={setRoleState}
                />
            ) : (
                <div>
                    <PageTitle
                        position={'center'}
                        title={locale.userManagement.selectRoleStepTitle}
                    />
                    <div className={styles.rolesContainer}>
                        {options.map(({ code, icon: Icon }) => {
                            return (
                                <button
                                    key={code}
                                    onClick={() =>
                                        code !== CUSTOM_ROLE &&
                                        handleSelectRole(code)
                                    }
                                    className={cx(styles.role, {
                                        [styles.selected]:
                                            selectedRole === code ||
                                            (code === CUSTOM_ROLE &&
                                                selectedCustomRole),
                                        [styles.roleCustomCursor]:
                                            code === CUSTOM_ROLE
                                    })}>
                                    <div className={styles.row}>
                                        <div className={styles.icon}>
                                            <Icon />
                                        </div>
                                        <div className={styles.roleDesc}>
                                            <h3>
                                                {code === CUSTOM_ROLE
                                                    ? locale.userManagement
                                                          .roleNames[code]
                                                    : locale.userManagement
                                                          .defaultRoles[code]}
                                            </h3>
                                            <p>
                                                {
                                                    locale.userManagement
                                                        .roleDescription[code]
                                                }
                                            </p>
                                            {code === CUSTOM_ROLE && (
                                                <div>
                                                    <div
                                                        className={
                                                            styles.pickerWrapper
                                                        }>
                                                        <Picker
                                                            renderValue={selected =>
                                                                selected &&
                                                                selected.id
                                                                    ? selected.name
                                                                    : locale
                                                                          .userManagement
                                                                          .selectExistingCustomRole
                                                            }
                                                            isWithEmptyValue={
                                                                true
                                                            }
                                                            key={
                                                                selectedCustomRole
                                                            }
                                                            className={
                                                                styles.picker
                                                            }
                                                            options={
                                                                availableCustomRoles
                                                            }
                                                            onChange={v =>
                                                                handleSelectCustomRole(
                                                                    v
                                                                )
                                                            }
                                                            value={
                                                                selectedCustomRole
                                                            }
                                                            withSearchAndSort={
                                                                true
                                                            }
                                                            isWithEmptyValueText=""
                                                            isSortingDisabled={
                                                                true
                                                            }
                                                        />
                                                    </div>
                                                    <div
                                                        className={
                                                            styles.createRoleButton
                                                        }
                                                        onClick={
                                                            handleCreateCustomRole
                                                        }>
                                                        {
                                                            locale
                                                                .userManagement
                                                                .createNewCustomrole
                                                        }
                                                        {
                                                            locale
                                                                .userManagement
                                                                .createCustomRole
                                                        }
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            )
                        })}
                    </div>
                </div>
            )}
            <div className={styles.buttonsBlock}>
                {isCustomRoleSelect ? (
                    <Button reverted onClick={() => handleSelectRole('')}>
                        {locale.userManagement.backToDefaultRoles}
                    </Button>
                ) : (
                    <Button onClick={previousStep}>
                        {locale.userManagement.stepBack}
                    </Button>
                )}
                <Button
                    disabled={
                        !isCustomRoleSelect &&
                        !selectedRole &&
                        !selectedCustomRole
                    }
                    onClick={handleNextStep}>
                    {locale.userManagement.nextStep}
                </Button>
            </div>
        </div>
    )
}

const useStyles = makeStyles(() => ({
    icon: {
        marginRight: 27
    },
    row: {
        display: 'flex',
        alignItems: 'center'
    },
    roleDesc: {
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column'
    },
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        padding: '40px 94px 0'
    },
    buttonsBlock: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 46,
        marginTop: 20,
        '&& > :last-child': {
            marginLeft: 16
        }
    },
    selected: {
        outline: `1px solid black !important`
    },
    rolesContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center'
    },
    role: {
        '&& h3': {
            margin: 0,
            fontSize: 18
        },
        '&& p': {
            fontSize: 15,
            textAlign: 'left'
        },
        '&&:hover': {
            transform: 'scale(1.03)',
            transition: 'transform 0.1s ease-out'
        },
        cursor: 'pointer',
        borderRadius: 5,
        margin: 10,
        padding: '37px 21px',
        minWidth: 150,
        width: '45%',
        maxWidth: 453,
        // height: 170,
        background: '#F9F9F9',
        border: 'none'
    },
    createRoleButton: {
        marginTop: 20,
        paddingLeft: 15,
        textDecoration: 'underline',
        fontSize: '12px',
        color: 'black',
        cursor: 'pointer'
    },
    pickerWrapper: {
        borderRadius: '10px',
        backgroundColor: 'white',
        padding: '0 1em'
    },
    roleCustomCursor: {
        cursor: 'default'
    }
}))

const getOptionsForCurrentRole = (
    role,
    relation,
    userRelationFromEmail,
    defaultRoles
) => {
    const adminRole = defaultRoles.find(
        r => r.code === DEFAULT_ROLES.customer_admin
    )
    const managerRole = defaultRoles.find(
        r => r.code === DEFAULT_ROLES.management_user
    )
    const technicianRole = defaultRoles.find(
        r => r.code === DEFAULT_ROLES.technician
    )
    const viewerRole = defaultRoles.find(r => r.code === DEFAULT_ROLES.viewer)

    const userOptions = [
        {
            roleId: adminRole.id,
            code: adminRole.code,
            icon: AdminUser
        },
        {
            roleId: viewerRole.id,
            code: viewerRole.code,
            icon: ViewerUser
        },
        {
            roleId: managerRole.id,
            code: managerRole.code,
            icon: ManagerUser
        },
        {
            roleId: technicianRole.id,
            code: technicianRole.code,
            icon: TechnicianUser
        },
        {
            code: CUSTOM_ROLE,
            icon: CustomUser
        }
    ]

    const isAdmin = [
        DEFAULT_ROLES.customer_admin,
        DEFAULT_ROLES.traplinked_admin
    ].includes(role)

    const isExternal = relation === USER_RELATIONS.external

    // when creating external user only 2 roles available to assign
    if (isExternal || userRelationFromEmail === RELATIONS.externalOut) {
        return userOptions.filter(
            r => r.code === viewerRole.code || r.code === technicianRole.code
        )
    }

    return isAdmin
        ? userOptions
        : userOptions.filter(i => i.code !== DEFAULT_ROLES.customer_admin)
}
